import React, { useMemo } from "react";
import styles from "./Menu.module.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAdmin } from "../../hooks/useAdmin";
import { useAdminAccessControl } from "../../hooks/useAdminAccessControl";
import { USER_ROLE } from "../../common/model/enum";
import {
  ADMIN_ROUTE_META,
  isAdminRouteActive,
} from "../../navigation/adminRouteMeta";
import { useZorni } from "../../../app/hooks/useZorni";
import { clearVisitedTrips } from "../../../app/components/tripDetails/detailsTrip/hooks/useVisitedTrips";
import zorniProLogo from "../../../assets/img/core-img/zorni-pro.png";
import mondovivaLogo from "../../../assets/img/core-img/Mondoviva-vert.png";

// components-lib
import { Text, Button, Icon, Avatar } from "@zorni/components-lib";

const Menu = ({
  onNavigate,
  onToggleCollapse,
  isCollapsed = false,
  isMobile = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { organization, hasPARole, isAuthUserOwner } = useAdmin();
  const { keycloak, email } = useZorni();

  const {
    personInfo,
    isAdminAccessBlocked,
    isOnlyEstablishmentStepPending,
    needsEstablishment,
  } = useAdminAccessControl();
  const isInvalidOrganization = !organization;
  const isInvalidEstablishment = needsEstablishment;

  const canManage =
    hasPARole || isAuthUserOwner || organization?.role == USER_ROLE.PSA;

  const pathname = location.pathname || "";
  const normalizedPathname = useMemo(
    () => pathname.replace(/\/+$/, ""),
    [pathname]
  );
  const routesByKey = useMemo(
    () =>
      ADMIN_ROUTE_META.reduce((accumulator, route) => {
        accumulator[route.key] = route;
        return accumulator;
      }, {}),
    []
  );

  const handleNavigate = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const handleLogout = () => {
    clearVisitedTrips();
    keycloak
      ?.logout?.({ redirectUri: `${window.location.origin}/` })
      .catch(() => {
        navigate("/");
      });
  };

  const profileName = useMemo(() => {
    const first = personInfo?.firstName || personInfo?.lastName || "";
    return first ? first.trim().charAt(0).toUpperCase() : "Mondoviva Admin";
  }, [personInfo]);

  const profileEmail = email || "";
  const canToggleCollapse = !isMobile && typeof onToggleCollapse === "function";

  const menuItems = useMemo(() => {
    const isPathActive = (path, exact = false) => {
      const normalizedTargetPath = path.replace(/\/+$/, "");
      if (!normalizedTargetPath) {
        return false;
      }

      if (exact) {
        return normalizedPathname === normalizedTargetPath;
      }

      return (
        normalizedPathname === normalizedTargetPath ||
        normalizedPathname.startsWith(`${normalizedTargetPath}/`)
      );
    };

    const organizationChildren = canManage
      ? [
          {
            key: "organisme",
            label: t(routesByKey.organisme.labelKey),
            icon: routesByKey.organisme.icon,
            active: isAdminRouteActive(pathname, routesByKey.organisme.segment),
            onClick: () => handleNavigate(routesByKey.organisme.path),
            warning: isInvalidOrganization,
            // Reste accessible quand seule l'étape 3 est à valider : c'est là
            // qu'on complète l'organisme.
            disabled: isAdminAccessBlocked && !isOnlyEstablishmentStepPending,
          },
          {
            key: "establishments",
            label: t(routesByKey.establishments.labelKey),
            icon: routesByKey.establishments.icon,
            active: isAdminRouteActive(pathname, routesByKey.establishments.segment),
            onClick: () => handleNavigate(routesByKey.establishments.path),
            warning: isInvalidEstablishment,
            disabled: isAdminAccessBlocked && !isOnlyEstablishmentStepPending,
          },
          {
            key: "utilisateurs",
            label: t(routesByKey.utilisateurs.labelKey),
            icon: routesByKey.utilisateurs.icon,
            active: isAdminRouteActive(pathname, routesByKey.utilisateurs.segment),
            onClick: () => handleNavigate(routesByKey.utilisateurs.path),
            disabled: isAdminAccessBlocked,
          },
        ]
      : [];

    const circuitsChildren = [
      {
        key: "new-circuit",
        label: t("dashboardMenu.newCircuit"),
        icon: "iconPath",
        active: isPathActive("/admin/circuits/add"),
        onClick: () => handleNavigate("/admin/circuits/add"),
        disabled: isAdminAccessBlocked,
      },
      {
        key: "template-circuit",
        label: t("dashboardMenu.circuitTemplate"),
        icon: "iconBooks",
        disabled: true,
        active: false,
      },
      {
        key: "my-circuits",
        label: t("dashboardMenu.myCircuits"),
        icon: routesByKey.circuits.icon,
        active:
          isPathActive("/admin/circuits") && !isPathActive("/admin/circuits/add"),
        onClick: () => handleNavigate(routesByKey.circuits.path),
        disabled: isAdminAccessBlocked,
      },
    ];

    return [
      {
        key: "dashboard",
        label: t(routesByKey.dashboard.labelKey),
        icon: routesByKey.dashboard.icon,
        active: isAdminRouteActive(pathname, routesByKey.dashboard.segment),
        onClick: () => handleNavigate(routesByKey.dashboard.path),
        disabled: isAdminAccessBlocked,
      },
      ...(organizationChildren.length
        ? [
            {
              key: "organization",
              label: t("dashboardMenu.organization"),
              icon: "iconBank",
              children: organizationChildren,
              active: organizationChildren.some((child) => child.active),
            },
          ]
        : []),
      {
        key: "circuits",
        label: t(routesByKey.circuits.labelKey),
        icon: routesByKey.circuits.icon,
        children: circuitsChildren,
        active: circuitsChildren.some((child) => child.active),
      },
      {
        key: "reservations",
        label: t(routesByKey.reservations.labelKey),
        icon: routesByKey.reservations.icon,
        active: isAdminRouteActive(pathname, routesByKey.reservations.segment),
        onClick: () => handleNavigate(routesByKey.reservations.path),
        disabled: isAdminAccessBlocked,
      },
      {
        key: "inbox",
        label: t(routesByKey.inbox.labelKey),
        icon: routesByKey.inbox.icon,
        active: isAdminRouteActive(pathname, routesByKey.inbox.segment),
        onClick: () => handleNavigate(routesByKey.inbox.path),
        disabled: isAdminAccessBlocked,
      },
      {
        key: "academy",
        label: t("dashboardMenu.digitalAcademy"),
        icon: "iconGraduationCap",
        disabled: true,
        active: false,
      },
    ];
  }, [
    canManage,
    handleNavigate,
    isAdminAccessBlocked,
    isOnlyEstablishmentStepPending,
    isInvalidEstablishment,
    isInvalidOrganization,
    normalizedPathname,
    pathname,
    routesByKey,
    t,
  ]);

  return (
    <nav
      className={[styles.menuRoot, isCollapsed ? styles.menuRootCollapsed : ""].join(" ")}
      aria-label={t("dashboardMenu.aria.nav")}
    >
      <div className={styles.menuCard}>
        <button
          type="button"
          className={styles.brandImageButton}
          onClick={() => handleNavigate("/admin")}
          aria-label={t("dashboardMenu.aria.brand")}
          title="Zorni Pro"
        >
          <img
            src={isCollapsed ? mondovivaLogo : zorniProLogo}
            alt={isCollapsed ? "Mondoviva" : "Zorni Pro"}
            className={styles.brandImage}
          />
        </button>

        <div className={styles.menuList}>
          <div className={styles.itemsBlock}>
            {menuItems.map((it) => (
              <React.Fragment key={it.key}>
                {it.children?.length > 0 ? (
                  <div className={styles.sectionTitle}>
                    <Text size="xs" weight="bold" color="muted">
                      {it.label}
                    </Text>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={[
                      styles.item,
                      it.active ? styles.itemActive : "",
                      it.disabled ? styles.itemDisabled : "",
                      !it.onClick && !it.disabled ? styles.itemStatic : "",
                    ].join(" ")}
                    onClick={it.disabled || !it.onClick ? undefined : it.onClick}
                    aria-current={it.active ? "page" : undefined}
                    aria-label={it.label}
                    title={it.label}
                  >
                    <span>
                      <Icon name={it.icon} size="md" color="inherit" />
                    </span>

                    <span className={styles.itemLabel}>
                      <Text
                        as="span"
                        size="sm"
                        weight={it.active ? "bold" : "medium"}
                        color="inherit"
                      >
                        {it.label}
                      </Text>
                    </span>

                    {it.warning && (
                      <span
                        className={styles.warning}
                        title={t("dashboardMenu.configurationRequired")}
                      >
                        <Icon name="iconWarning" size="sm" color="danger" />
                      </span>
                    )}
                  </button>
                )}

                {it.children?.length > 0 && (
                  <div className={styles.childItems}>
                    {it.children.map((child) => (
                      <button
                        key={child.key}
                        type="button"
                        className={[
                          styles.item,
                          styles.itemChild,
                          child.active ? styles.itemActive : "",
                          child.disabled ? styles.itemDisabled : "",
                        ].join(" ")}
                        onClick={
                          child.disabled || !child.onClick ? undefined : child.onClick
                        }
                        aria-current={child.active ? "page" : undefined}
                        aria-label={child.label}
                        title={child.label}
                      >
                        <span>
                          <Icon name={child.icon} size="md" color="inherit" />
                        </span>

                        <span className={styles.itemLabel}>
                          <Text
                            as="span"
                            size="sm"
                            weight={child.active ? "bold" : "medium"}
                            color="inherit"
                          >
                            {child.label}
                          </Text>
                        </span>

                        {child.warning && (
                          <span
                            className={styles.warning}
                            title={t("dashboardMenu.configurationRequired")}
                          >
                            <Icon name="iconWarning" size="sm" color="danger" />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className={styles.footer}>
          <div className={styles.profileCard}>
            <Avatar
              name={profileName}
              size="md"
              dot
              dotColor="success"
              onClick={() => handleNavigate("/profil")}
              className={styles.profileAvatarClickable}
            />
            <div className={styles.profileText}>
              <Text
                as="span"
                size="sm"
                weight="semibold"
                className={styles.profileNameClickable}
                onClick={() => handleNavigate("/profil")}
              >
                {profileName}
              </Text>
              <Text as="span" size="xs" color="muted">
                {profileEmail}
              </Text>
            </div>
            <Button
              variant="ghost"
              size="small"
              iconName="iconSignOut"
              onClick={handleLogout}
              aria-label={t("dashboardMenu.aria.logout")}
              className={styles.logoutBtn}
            />
          </div>

          {canToggleCollapse && (
            <button
              type="button"
              className={styles.collapseToggle}
              onClick={onToggleCollapse}
              aria-label={
                isCollapsed ? "Agrandir la barre laterale" : "Reduire la barre laterale"
              }
              title={isCollapsed ? "Agrandir la sidebar" : "Reduire la sidebar"}
            >
              <span className={styles.collapseToggleIcon}>
                <Icon
                  name={isCollapsed ? "iconChevronRight" : "iconChevronLeft"}
                  size="sm"
                  color="inherit"
                />
              </span>
              <span className={styles.collapseToggleLabel}>
                <Text as="span" size="sm" weight="semibold" color="inherit">
                  {isCollapsed ? "Agrandir" : "Reduire"}
                </Text>
              </span>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Menu;
